// Pure default value factories — no Zustand dependency.
// Shared between frontend (store.ts) and backend (presalesDashboard worker).

import type {
  AmlConfig,
  BiometricsConfig,
  BlueprintConfig,
  BlueprintDataInput,
  BlueprintMaturityDimension,
  BlueprintVerificationLayer,
  GeneralInfo,
  GanttTask,
  KybConfig,
  KycConfig,
  RaciColumn,
  RaciEntry,
  RaciValue,
  Recipe,
  RiskConfig,
  RiskLevelRule,
  RiskScoringMatrixRow,
  IndividualRiskModelRow,
  CustomRiskFactor,
  RulesConfig,
  ScopeData,
  SowConfig,
} from '../types'

export const getDefaultGeneralInfo = (): GeneralInfo => ({
  presalesHours: 0,
  productionAccessDate: '',
  goLiveDate: '',
  isHardGoLiveDate: false,
  isUpsell: false,
  scopeConfirmed: false,
  scopeStatus: 'draft',
  scopeStatusDates: {
    draft: new Date().toISOString(),
    sent: null,
    adjust: null,
    confirm: null,
    handover: null,
    implementations: null,
  },
  forecastChecks12m: null,
  parentChild: 'unknown',
  parentEntity: '',
  childEntity: '',

  testDetails: '',

  integrationType: 'unknown',
  aiFeatures: 'unknown',
  platformVersion: 'unknown',
  ssoAccess: 'unknown',

  parentAccountName: '',
  parentUatId: '',
  parentProdId: '',
  childAccountName: '',
  childUatId: '',
  childProdId: '',

  clientUseCase: '',
  phases: [''],

  sowVariationNote: '',

  resellerAgreement: 'unknown',
  resellerName: '',
  resellerRelationshipType: 'unknown',
  resellerPredefinedConfig: 'unknown',
  resellerChildAccountStructure: 'unknown',

  billingRequirements: '',

  portalAccessUsers: [],

  customWorks: [],

  presalesActions: [],
})

export const DEFAULT_DATA_INPUT_TYPES = [
  'First name', 'Middle name', 'Last name', 'DOB', 'Address',
  'Email', 'Phone', 'National ID / SSN', 'Document scan', 'Selfie', 'Device data',
]

export const getDefaultDataInputs = (): BlueprintDataInput[] =>
  DEFAULT_DATA_INPUT_TYPES.map((type) => ({ type, enabled: false, confirmed: false, fuzziness: 'unknown' as const, captureMethod: '', addressFormat: '', notes: '' }))

export const DEFAULT_PREP_CHECKLIST: BlueprintConfig['prepChecklist'] = [
  { id: 'read_transcripts', label: 'Read all prior transcripts, emails, Slack threads, and internal notes in full', checked: false },
  { id: 'asis_diagram', label: 'As-is architecture diagram updated with latest understanding', checked: false },
  { id: 'committee_map', label: 'Technical stakeholder map reviewed and updated', checked: false },
  { id: 'regulatory_events', label: 'Regulatory / market events identified for their geos and timeline', checked: false },
  { id: 'competitive_landscape', label: 'Customer competitive landscape, current vendors, and recent announcements researched', checked: false },
  { id: 'questions_ranked', label: 'Specific architectural questions written and ranked by priority', checked: false },
  { id: 'team_briefed', label: 'Everyone going on the call has been briefed — nobody walks in cold', checked: false },
]

export const VERIFICATION_STACK_LAYERS = [
  'Document capture & liveness',
  'Identity verification (bureau/govt)',
  'Biometric match',
  'Device & behavioural signals',
  'Sanctions / PEP / adverse media',
  'Decisioning & orchestration',
  'Case management & ongoing monitoring',
  'Business onboarding (KYB)',
] as const

export const getDefaultVerificationStack = (): BlueprintVerificationLayer[] =>
  VERIFICATION_STACK_LAYERS.map((layer) => ({ id: `vs_${layer.replace(/\W+/g, '_').toLowerCase()}`, layer, customLabel: '', vendor: '', owner: '', painPoints: '' }))

export const MATURITY_DIMENSIONS = [
  { id: 'verification_architecture', dimension: 'Verification architecture', tooltip: 'How verification steps are structured — from a flat checklist of manual checks through to an adaptive, orchestrated flow that adjusts based on risk signals in real time.', stages: ['Checklist', 'Stacked sequential', 'Orchestrated parallel', 'Adaptive'] },
  { id: 'decisioning', dimension: 'Decisioning', tooltip: 'How pass/fail/review decisions are made — from hard-coded if/else rules through to ML-driven scoring that adapts to new fraud patterns and population shifts.', stages: ['Hard-coded rules', 'Rule engine', 'Risk-weighted scoring', 'Adaptive ML'] },
  { id: 'fraud_signals', dimension: 'Fraud signals', tooltip: 'What signals are used to detect fraud beyond identity verification — from no fraud layer through to continuous behavioural analytics across the customer lifecycle.', stages: ['None / basic', 'Device + email', 'Multi-signal', 'Continuous behavioural'] },
  { id: 'ongoing_monitoring', dimension: 'Ongoing monitoring', tooltip: 'How customers are monitored after onboarding — from annual batch re-screening through to continuous, adaptive monitoring that adjusts frequency and depth based on risk.', stages: ['Periodic batch', 'Triggered review', 'Continuous', 'Continuous + adaptive'] },
  { id: 'regulatory_posture', dimension: 'Regulatory posture', tooltip: 'How the organisation approaches regulation — from reactive (fixing issues after audits) through to actively shaping industry standards and regulatory frameworks.', stages: ['Reactive', 'Compliant', 'Proactive', 'Shaping'] },
] as const

export const getDefaultMaturityDimensions = (): BlueprintMaturityDimension[] =>
  MATURITY_DIMENSIONS.map((d) => ({ id: d.id, dimension: d.dimension, stage: 0, evidence: '' }))

export const getDefaultBlueprintConfig = (): BlueprintConfig => ({
  businessModel: '',
  monthlyOnboardingVolume: '',
  strategicPriorities: '',
  strategicPrioritiesSummary: '',
  stpRate: '',
  stpRateConfirmed: false,
  fraudRate: '',
  fraudRateConfirmed: false,
  problemHypothesis: '',
  problemHypothesisSummary: '',
  surfacePain: '',
  surfacePainSummary: '',
  deepPain: '',
  deepPainSummary: '',
  whyNow: '',
  whyNowSummary: '',
  regulatoryContext: '',
  regulatoryContextSummary: '',
  regulatoryIntel: '',
  currentProcess: '',
  currentProcessSummary: '',
  miroLink: '',
  currentVendors: [],
  integrationMethod: '',
  jurisdictionsInScope: '',
  onboardingJourney: '',
  checksBeforeTransact: '',
  riskSegmentation: '',
  eddTriggers: '',
  sanctionsPepScreening: '',
  ongoingMonitoring: '',
  frictionFalsePositives: '',
  verificationStack: getDefaultVerificationStack(),
  signals: [],
  countryRulesets: [],
  maturityDimensions: getDefaultMaturityDimensions(),
  priorityGaps: [],
  quickWins: [],
  foundationMoves: [],
  strategicShifts: [],
  stakeholders: [],
  postCallUpdates: [],
  prepChecklist: DEFAULT_PREP_CHECKLIST.map((c) => ({ ...c })),
})

export const getDefaultKycConfig = (): KycConfig => ({
  kycRequired: 'unknown',
  isReportingEntity: 'unknown',
  documentTypesAu: ['unknown'],
  enableConsentSources: 'unknown',
  acceptNzCustomers: 'unknown',
  documentTypesNz: ['unknown'],
  dvsForeignPassport: 'unknown',
  internationalCountries: ["Don't know"],

  auOtherDocumentTypesEnabled: false,
  auOtherDocumentTypesText: '',

  internationalDocumentAcceptance: {},

  ruleset: '',
  customRuleset: '',
  vendor: 'unknown',
  otherVendor: '',

  complianceFramework: 'unknown',
  specificRegulatoryRequirements: '',

  // KYC Ruleset Builder - Support multiple rulesets
  rulesets: [],

  requiredDataElements: {
    fullName: 'unknown',
    middleName: 'unknown',
    dateOfBirth: 'unknown',
    address: 'unknown',
    governmentId: 'unknown',
  },
  minimumDataSources: 'unknown',
  minimumDataElementsPerSource: 'unknown',

  nameMatchingTolerance: 'tbd',
  dobMatchingTolerance: 'tbd',
  addressMatchingTolerance: 'tbd',
  passFailCriteria: '',

  primaryCustomerJurisdictions: ["Don't know"],
  geographicCoverageNotes: '',

  auGovernmentSources: {
    dvsDirect: 'unknown',
    dvsViaEquifax: 'unknown',
    vevo: 'unknown',
    electoralRoll: 'unknown',
  },
  creditBureauSources: {
    equifaxCreditHeader: 'unknown',
    equifaxCreditHeaderVia: 'unknown',
    equifaxCreditHeaderDirectNote: '',
    experianCreditHeader: 'unknown',
    experianCreditHeaderVia: 'unknown',
    experianCreditHeaderDirectNote: '',
  },
  alternativeAuSources: {
    superchoice: 'unknown',
    superchoiceVia: 'unknown',
    superchoiceDirectNote: '',
    equifaxCommercialPublicRecords: 'unknown',
    equifaxCommercialPublicRecordsVia: 'unknown',
    equifaxCommercialPublicRecordsDirectNote: '',
    tenancyDatabase: 'unknown',
    tenancyDatabaseVia: 'unknown',
    tenancyDatabaseDirectNote: '',
    phoneDirectoryData: 'unknown',
    phoneDirectoryDataVia: 'unknown',
    phoneDirectoryDataDirectNote: '',
    datazoo: 'unknown',
    datazooVia: 'unknown',
    datazooDirectNote: '',
  },
  internationalDataSources: {
    gdc: 'unknown',
    gdcVia: 'unknown',
    gdcDirectNote: '',
    centrix: 'unknown',
    centrixVia: 'unknown',
    centrixDirectNote: '',
    equifaxNz: 'unknown',
    equifaxNzVia: 'unknown',
    equifaxNzDirectNote: '',
    asiaVerify: 'unknown',
    asiaVerifyVia: 'unknown',
    asiaVerifyDirectNote: '',
    monnai: 'unknown',
    monnaiVia: 'unknown',
    monnaiDirectNote: '',
    otherRegionalProviders: 'unknown',
    otherRegionalProvidersVia: 'unknown',
    otherRegionalProvidersDirectNote: '',
  },
  dataSourceNotes: '',

  enableCascade: 'tbd',
  cascadeStrategy: 'tbd',
  sourcePriorityOrder: [],
  cascadeNotes: '',

  manualReviewTriggers: {
    partialMatchesBelowThreshold: 'unknown',
    nameVariationsDetected: 'unknown',
    addressMismatches: 'unknown',
    documentVerificationFailures: 'unknown',
    pepSanctionsHits: 'unknown',
    multipleFailedAttempts: 'unknown',
    highRiskJurisdictions: 'unknown',
    expiredDocuments: 'unknown',
    ageVerificationIssues: 'unknown',
    other: 'unknown',
  },
  manualReviewThreshold: 'tbd',
  manualReviewNotes: '',

  specialRequirements: '',
  configurationStatus: 'unknown',
  configurationNotes: '',

  agingEnabled: true,
  agingPeriodMonths: 12,
  agingCustomConfig: '',

  selectedDataSources: [],
})

export const getDefaultAmlConfig = (): AmlConfig => ({
  level: 'unknown',
  pepLevels: ['1', '2', '3', '4'],
  monitoringType: 'unknown',
  fuzziness: 30,
  storeInAustralia: false,
  sendCountriesForPep: true,
  customBlockList: '',
  relationship: 'unknown',
  vendor: 'unknown',
  otherVendor: '',

  amlRegulatoryRequirement: 'unknown',

  screeningEntityTypes: {
    individuals: 'unknown',
    businesses: 'unknown',
    ubos: 'unknown',
    directorsAndOfficers: 'unknown',
    authorizedRepresentatives: 'unknown',
  },

  riskBasedScreening: 'tbd',

  pepScreeningConfig: {
    enabled: 'unknown',
    includeFamilyMembers: 'yes',
    includeHistoricalPeps: 'yes',
  },

  sanctionsScreeningConfig: {
    enabled: 'unknown',
    listsToInclude: [],
    customLists: '',
    matchAction: 'manual_review_required',
  },

  adverseMediaScreeningConfig: {
    enabled: 'unknown',
    categories: [],
    customCategories: '',
    category: 'standard',
    matchAction: 'manual_review_required',
  },

  additionalWatchlistsConfig: {
    enabled: 'unknown',
    types: [],
  },

  ongoingMonitoringEnabled: 'unknown',
  ongoingMonitoringEnrolment: '',
  ongoingMonitoringFrequency: 'tbd',
  ongoingMonitoringScope: [],
  monitoringAutoDisableRules: [],
  monitoringAlertHandling: 'tbd',

  nameMatchingStrategy: {
    includeMiddleNames: 'tbd',
    transliterationSupport: 'tbd',
  },
  geographicDataEnhancement: 'tbd',
  dobMatchingConfig: 'tbd',
  falsePositiveManagement: [],

  vendorSelectionRationale: [],
  vendorSpecificFeatures: {
    complyAdvantage: {
      standardPepSanctions: false,
      addAdverseMedia: false,
      enableOngoingMonitoring: false,
      customSearchProfiles: false,
    },
    refinitiv: {
      worldCheck: false,
      mediaCheck: false,
      enableOngoingMonitoring: false,
      customScreeningGroups: false,
    },
    dowJones: {
      riskCompliance: false,
      factiva: false,
      customWatchlists: false,
    },
    acuris: {
      pep: false,
      sanctions: false,
    },
  },
  multiVendorStrategy: 'tbd',
  vendorRegistrationRequired: 'unknown',

  auditTrailRequirements: [],
  reportingRequirements: [],
  suspiciousActivityThresholds: '',
  dataRetentionPeriod: 'tbd',

  screeningTiming: 'tbd',
  matchResolutionWorkflow: 'tbd',

  batchProcessing: {
    required: 'unknown',
    details: '',
  },

  jurisdictionScreening: {
    screenAllJurisdictions: 'unknown',
    specificJurisdictions: '',
  },

  apiIntegrationRequirements: [],
  portalAccessRequirements: [],

  costOptimizationPriority: 'tbd',
  volumeEstimates: {
    initialScreeningVolume: null,
    ongoingMonitoringVolume: null,
    expectedMatchRate: 'unknown',
  },
  costAllocation: 'tbd',

  minorScreeningConfig: 'tbd',
  deceasedCustomerHandling: 'tbd',
  corporateEntityScreening: [],
  highRiskJurisdictionHandling: 'tbd',

  specialRequirements: '',
  configurationStatus: 'unknown',
  configurationNotes: '',
  lastUpdated: '',
  updatedBy: '',

  agingEnabled: true,
  agingPeriodMonths: 12,
  agingCustomConfig: '',
})

export const getDefaultBiometricsConfig = (): BiometricsConfig => ({
  enabled: 'unknown',
  serviceLevel: '',
  flows: [],
  documentTypes: [],
  smsSettingsConfirmed: 'unknown',
  customSmsNotRequired: false,
  customSmsMessageTemplate: `Header: Verify ID
Message: Hi {{customer_name}}, You're almost there. We just need you to head to {{onboarding_url}} within the next {{link_validity_hours}} hours to complete the verification process.`,
  smsLinkValidityHours: null,
  frontend: 'unknown',
  otherFrontend: '',
  platform: 'unknown',
  vendor: 'unknown',
  otherVendor: '',
  vendorNote: '',
  rebrandingHostedOneSDK: 'unknown',
  rebrandingDaon: 'unknown',
  extendedBranding: 'unknown',
  customRedirectPage: 'unknown',
  customConsents: 'unknown',
  brandingNotes: '',
  daonEngagementNotes: '',
})

export const getDefaultKybConfig = (): KybConfig => ({
  enabled: 'unknown',
  auChecksEnabled: 'unknown',
  auChecks: { amlStandard: false, amlEnhanced: false, asicInfo: false, ubo: false, creditReport: false },
  kycForIndividualsRequired: 'unknown',
  uboConsentAware: 'unknown',
  uboSmsPhoneAware: 'unknown',
  uboWorkflowId: '',
  amlEntityScreening: 'unknown',
  amlEntityScreeningTypes: [],
  entityTypesEnabled: 'unknown',
  entityTypeSplit: { soleTradersPercent: 0, ptyLtdPercent: 0, companyPercent: 0, partnershipPercent: 0, trustPercent: 0, otherPercent: 0 },
  otherEntityType: '',
  internationalEnabled: 'unknown',
  internationalCountryProducts: [],
  trustAnalyserEnabled: 'unknown',
  trustTypeSplit: { unitPercent: 0, discretionaryPercent: 0, smsfPercent: 0, otherPercent: 0 },
  onboardingFormEnabled: 'unknown',
  onboardingFormBranded: 'unknown',
  kybAmlOngoingMonitoringEnabled: 'unknown',
  kybAmlOngoingMonitoringNotes: '',
  continuousKybEnabled: 'unknown',
  continuousKybNotes: '',
  agingEnabled: true,
  agingPeriodMonths: 12,
  agingCustomConfig: '',
})

export const getDefaultRecipe = (): Recipe => ({
  id: Date.now(),
  name: 'Standard',
  accountType: 'Reporting Entity',
  products: {
    kyc: true,
    govId: true,
    vevo: false,
    internationalKyc: false,
    amlStandard: true,
    amlEnhanced: false,
    biometrics: false,
    ccr: true,
    blocklists: true,
    duplicates: true,
    supportDocs: false,
  },
  ruleset: '2x2_gov_id',
  customRuleset: '',
})

export const getDefaultRulesConfig = (): RulesConfig => ({
  duplicateRules: 'unknown',
  customDuplicateRules: '',
  customDuplicateRuleSet: [],
  customDuplicateNotes: '',
  blocklistRules: 'unknown',
  customBlocklistRules: '',
  customBlocklistEntries: [],
  customBlocklistNotes: '',

  riskRatingSystem: 'unknown',
  riskSystemChange: 'unknown',

  fraudEnabled: 'unknown',
  fraudTypes: [],
  transactionMonitoringEnabled: 'unknown',

  fraudRequirements: '',
  transactionMonitoringRequirements: '',
})

export const getDefaultRiskConfig = (): RiskConfig => ({
  riskLevels: [
    {
      id: 'unacceptable',
      label: 'Unacceptable',
      threshold: 75,
      ratingMin: 75,
      ratingMax: null,
      statusResponse: 'Fail',
    },
    {
      id: 'high',
      label: 'High',
      threshold: 51,
      ratingMin: 51,
      ratingMax: 75,
      statusResponse: 'Needs attention',
    },
    {
      id: 'medium',
      label: 'Medium',
      threshold: 31,
      ratingMin: 31,
      ratingMax: 50,
      statusResponse: 'Passed',
    },
    {
      id: 'low',
      label: 'Low',
      threshold: 1,
      ratingMin: 1,
      ratingMax: 30,
      statusResponse: 'Passed',
    },
    {
      id: 'unknown_unchecked',
      label: 'Unknown / Unchecked',
      threshold: 0,
      ratingMin: 0,
      ratingMax: 0,
      statusResponse: 'Unchecked',
    },
  ] satisfies RiskLevelRule[],

  individualModel: [
    { factor: 'Blocklist', outcome: 'None', rating: 0 },
    { factor: 'Internal watchlist', outcome: 'ID theft victim', rating: 45 },
    { factor: 'IDV Ocr Extract', outcome: 'None', rating: 0 },
    { factor: 'IDV validation', outcome: 'None', rating: 0 },
    { factor: 'IDV photo comparison', outcome: 'None', rating: 0 },
    { factor: 'KYC', outcome: 'Pass', rating: 0 },
    { factor: 'PEP Risk', outcome: 'PEP', rating: 30 },
    { factor: 'Sanctions Risk', outcome: 'None', rating: 0 },
    { factor: 'Watchlist', outcome: 'None', rating: 0 },
    { factor: 'Adverse Media', outcome: 'None', rating: 0 },
    { factor: 'Device', outcome: 'None', rating: 0 },
    { factor: 'Email', outcome: 'None', rating: 0 },
    { factor: 'Phone', outcome: 'None', rating: 0 },
  ] satisfies IndividualRiskModelRow[],

  scoringMatrix: [
    { factorGroup: 'AML Screening', reviewFactor: 'PEP Risk', criteria: 'PEP', pointsAllocated: 30 },
    { factorGroup: 'AML Screening', reviewFactor: 'PEP Risk', criteria: 'None', pointsAllocated: 0 },
    { factorGroup: 'AML Screening', reviewFactor: 'Sanctions Risk', criteria: 'Sanction', pointsAllocated: 100 },
    { factorGroup: 'AML Screening', reviewFactor: 'Sanctions Risk', criteria: 'None', pointsAllocated: 0 },
    { factorGroup: 'AML Screening', reviewFactor: 'Watchlist', criteria: 'Watchlist', pointsAllocated: 50 },
    { factorGroup: 'AML Screening', reviewFactor: 'Watchlist', criteria: 'None', pointsAllocated: 0 },
    { factorGroup: 'AML Screening', reviewFactor: 'Adverse Media', criteria: 'Adverse Media', pointsAllocated: 10 },
    { factorGroup: 'AML Screening', reviewFactor: 'Adverse Media', criteria: 'None', pointsAllocated: 0 },
    { factorGroup: 'KYC', reviewFactor: 'KYC', criteria: 'Partial', pointsAllocated: 10 },
    { factorGroup: 'KYC', reviewFactor: 'KYC', criteria: 'No Match', pointsAllocated: 15 },
    { factorGroup: 'KYC', reviewFactor: 'KYC', criteria: 'Pass', pointsAllocated: 0 },
    { factorGroup: 'Biometrics / IDV', reviewFactor: 'IDV Ocr Extract', criteria: 'Suspected', pointsAllocated: 10 },
    { factorGroup: 'Biometrics / IDV', reviewFactor: 'IDV Ocr Extract', criteria: 'Rejected', pointsAllocated: 25 },
    { factorGroup: 'Biometrics / IDV', reviewFactor: 'IDV Ocr Extract', criteria: 'None', pointsAllocated: 0 },
    { factorGroup: 'Biometrics / IDV', reviewFactor: 'IDV validation', criteria: 'Suspected', pointsAllocated: 10 },
    { factorGroup: 'Biometrics / IDV', reviewFactor: 'IDV validation', criteria: 'Rejected', pointsAllocated: 25 },
    { factorGroup: 'Biometrics / IDV', reviewFactor: 'IDV validation', criteria: 'None', pointsAllocated: 0 },
    { factorGroup: 'Biometrics / IDV', reviewFactor: 'IDV photo comparison', criteria: 'Suspected', pointsAllocated: 10 },
    { factorGroup: 'Biometrics / IDV', reviewFactor: 'IDV photo comparison', criteria: 'Rejected', pointsAllocated: 30 },
    { factorGroup: 'Biometrics / IDV', reviewFactor: 'IDV photo comparison', criteria: 'None', pointsAllocated: 0 },
    { factorGroup: 'Platform', reviewFactor: 'BlockList', criteria: 'Blocklist', pointsAllocated: 100 },
    { factorGroup: 'Platform', reviewFactor: 'BlockList', criteria: 'None', pointsAllocated: 0 },
    { factorGroup: 'Platform', reviewFactor: 'Internal watchlist', criteria: 'On watchlist', pointsAllocated: 30 },
    { factorGroup: 'Platform', reviewFactor: 'Internal watchlist', criteria: 'Former blocklist', pointsAllocated: 35 },
    { factorGroup: 'Platform', reviewFactor: 'Internal watchlist', criteria: 'Suspected fraud', pointsAllocated: 45 },
    { factorGroup: 'Platform', reviewFactor: 'Internal watchlist', criteria: 'Suspected ID theft', pointsAllocated: 45 },
    { factorGroup: 'Platform', reviewFactor: 'Internal watchlist', criteria: 'ID theft victim', pointsAllocated: 45 },
    { factorGroup: 'Platform', reviewFactor: 'Internal watchlist', criteria: 'Susicious data', pointsAllocated: 45 },
    { factorGroup: 'Platform', reviewFactor: 'Internal watchlist', criteria: 'SMR raised', pointsAllocated: 45 },
    { factorGroup: 'Platform', reviewFactor: 'Internal watchlist', criteria: '2x SMR reports lodged', pointsAllocated: 60 },
    { factorGroup: 'Platform', reviewFactor: 'Internal watchlist', criteria: 'None', pointsAllocated: 0 },
    { factorGroup: 'Fraud', reviewFactor: 'Device', criteria: 'High', pointsAllocated: 45 },
    { factorGroup: 'Fraud', reviewFactor: 'Device', criteria: 'Med', pointsAllocated: 25 },
    { factorGroup: 'Fraud', reviewFactor: 'Device', criteria: 'Low', pointsAllocated: 10 },
    { factorGroup: 'Fraud', reviewFactor: 'Device', criteria: 'None', pointsAllocated: 0 },
    { factorGroup: 'Fraud', reviewFactor: 'Email', criteria: 'High', pointsAllocated: 45 },
    { factorGroup: 'Fraud', reviewFactor: 'Email', criteria: 'Med', pointsAllocated: 25 },
    { factorGroup: 'Fraud', reviewFactor: 'Email', criteria: 'Low', pointsAllocated: 10 },
    { factorGroup: 'Fraud', reviewFactor: 'Email', criteria: 'None', pointsAllocated: 0 },
    { factorGroup: 'Fraud', reviewFactor: 'Phone', criteria: 'High', pointsAllocated: 45 },
    { factorGroup: 'Fraud', reviewFactor: 'Phone', criteria: 'Med', pointsAllocated: 25 },
    { factorGroup: 'Fraud', reviewFactor: 'Phone', criteria: 'Low', pointsAllocated: 10 },
    { factorGroup: 'Fraud', reviewFactor: 'Phone', criteria: 'None', pointsAllocated: 0 },
  ] satisfies RiskScoringMatrixRow[],

  customFactors: [] satisfies CustomRiskFactor[],
  notes: '',
  customRiskSheetUrl: '',
})

export const DEFAULT_SOW_LEGAL_TEXT = `This Statement of Work ("SOW") is issued under the terms of the Agreement between Frankie Financial Pty Ltd ("FrankieOne") and [Client Name][ABN] dated on or around [Insert Date] ("Agreement"), and covers the Configurations defined in the attached Configuration outline. Capitalised terms used in this SOW have the meaning given to them in the Agreement unless otherwise defined herein. The Effective Date of this Statement of Work shall be the date of the Client's Signature.

1. Purpose and Scope

This Statement of Work outlines the Professional Services to be provided by FrankieOne to support the Client in implementing and integrating the FrankieOne Platform into its systems. These services are designed to enable the Client to utilise specific modules of the FrankieOne Platform, in accordance with the requirements identified through collaborative workshops and planning sessions. The implementation will include project planning, technical configuration, user enablement, compliance alignment, stakeholder engagement, and post-go-live support. The objective is to deliver a production-ready implementation that meets the Client's compliance, business, operational, and onboarding requirements.

2. Scope of Services

FrankieOne will deliver the following services to the Client:

2.1. Project Planning and Discovery

FrankieOne and the Client will conduct an initial project kick-off and discovery workshops with the Client's stakeholders. In such workshops, FrankieOne and the Client will align on implementation goals, regulatory use cases, and integration points. Additionally, FrankieOne and the Client will Identify customer onboarding and risk monitoring objectives. Such workshops will be documented and provide agreement on technical and business requirements.

2.2. Environment and Account Configuration

FrankieOne will provision a test ("UAT") and production environments for the Client. During configuration, FrankieOne will set up of one (1) parent account and one (1) child account per environment. FrankieOne will configure the Client's accounts to include the required modules and services:

a) Identity Verification (IDV);
b) Branding configurations (white-label UI, logo, colours);
c) KYC data sources and orchestration settings;
d) AML screening rules, including PEP, sanctions, and adverse media lists;
e) Baseline transaction monitoring rules;
f) Customer Risk Rating (Standard CRR model); and
g) Agreed orchestration workflow ("Recipe").

2.3. Technical Integration Support

FrankieOne will provide API documentation and developer guidance throughout integration and support the Client's technical teams with integration to FrankieOne APIs. FrankieOne will validate integration logic and conduct walkthroughs as needed.

2.4. Testing and Validation

FrankieOne will assist in System Integration Testing (SIT) by providing test scenarios, data, and validation support. FrankieOne will also provide support during User Acceptance Testing (UAT) and Production Verification Testing (PVT), where FrankieOne will address defects or configuration issues raised during testing phases.

2.5. Training and Enablement

FrankieOne will deliver training sessions to administrators and core users. FrankieOne will provide the Client with FrankieOne user guides, compliance enablement materials, and workflow documentation. During Training and Enablement, FrankieOne will clarify usage boundaries, audit access, and escalation paths for various users.

2.6. Go-Live and Hypercare Support

FrankieOne will deploy configured workflows and rules to the production environment and provide support to the Client during the first two (2) weeks post go-live ("Hypercare Period") to Resolve early-life issues and ensure transition to operational support.

2.7. Compliance and Governance Alignment

FrankieOne will map FrankieOne's platform capabilities against the Client's regulatory use cases (e.g., AML/CTF, eKYC, transaction monitoring). FrankieOne will support the Client's internal compliance stakeholder sessions to validate end-to-end audit trails, recordkeeping, and reporting pathways. During Compliance and Governance Alignment, the parties will confirm the Client's readiness for onboarding regulated customers.

2.8. Stakeholder Communication and Reporting

Provide weekly status reports and progress updates.
- Facilitate regular steering meetings to track key risks, issues, and decision points.
- Coordinate between Client project, compliance, and IT teams as required.

2.9 Disclaimers
The parties acknowledge and agree that:
i) any delays caused by the Client (including failure to provide required information, feedback, or approvals within agreed timeframes) may result in adjustments to the project timeline and/or additional Fees; and
ii) FrankieOne's obligations under this SOW are subject to the Client's compliance with its obligations under the Agreement and this SOW.

3. Out of Scope
The following services are explicitly excluded from the scope of this SOW unless otherwise agreed in writing:
a) Configuration of custom risk models or workflows beyond the standard CRR model;
b) Development of custom features or services not currently supported by the FrankieOne API;
c) Data migration services from the Client's legacy systems;
d) Integration with third-party platforms or services outside of those offered within FrankieOne's platform;
e) Hosting or support of any customer-managed systems; and
f) End-user onboarding and customer success management post go-live.

4. Change Management
4.1. Change Requests
Either party may request changes to the scope, timeline, or deliverables of this SOW by submitting a written Change Request. All Change Requests must include a description of the proposed change, the reason for the change, and any anticipated impact on cost, timeline, or deliverables.
4.2. Impact Assessment
Upon receipt of a Change Request, FrankieOne will conduct an impact assessment and provide the Client with an estimate of the effect on project cost, timeline, resources, and deliverables within five (5) business days. Both parties agree to negotiate any changes in good faith and in a timely manner.
4.3. Approval and Implementation
No change shall be implemented until the Change Request has been approved in writing by authorised representatives of both FrankieOne and the Client. Approved changes will be documented as an amendment to this SOW.
4.4. Disputed Changes
In the event of disagreement regarding a proposed change, the matter shall be escalated in accordance with the escalation procedures outlined in the Agreement. Pending resolution, both parties will continue to perform their obligations under the existing SOW unless otherwise agreed.
4.5. Minor Adjustments
Minor adjustments that do not materially affect the scope, cost, or timeline of the project may be agreed upon informally between the respective Project Managers, provided such adjustments are documented in the project status report.

5. Client Obligations
To enable FrankieOne to perform its obligations under this SOW effectively, the Client agrees:
i) to provide timely access to systems, environments, data, and personnel as reasonably required by FrankieOne to perform the services;
ii) that it will be responsible for obtaining any third-party consents, licences, or approvals necessary for FrankieOne to perform the services;
iii) that it will assign a dedicated project manager or coordinator to act as FrankieOne's primary point of contact;

6. Acceptance
Unless otherwise specified, deliverables under this SOW will be deemed accepted if the Client does not provide written notice of rejection (with reasonable detail of deficiencies) within ten (10) business days of delivery. If a deliverable is rejected, FrankieOne will use reasonable efforts to address the identified deficiencies and resubmit the deliverable within a mutually agreed timeframe.`

export const DEFAULT_UPSELL_SOW_LEGAL_TEXT = DEFAULT_SOW_LEGAL_TEXT

export const DEFAULT_RESELLER_SOW_LEGAL_TEXT = `This Statement of Work ("SOW") is issued under the terms of the Agreement between Frankie Financial Pty Ltd ("FrankieOne") and [Reseller Name][ABN] dated on or around [Insert Date] ("Agreement"), and covers the Configurations defined in the attached Configuration outline. Capitalised terms used in this SOW have the meaning given to them in the Agreement unless otherwise defined herein. The Effective Date of this Statement of Work shall be the date of the Reseller's Signature.

1. Purpose and Scope

This Statement of Work outlines the Professional Services to be provided by FrankieOne to support the Reseller in implementing and integrating the FrankieOne Platform into its systems. These services are designed to enable the Reseller to utilise specific modules of the FrankieOne Platform, in accordance with the requirements identified through collaborative workshops and planning sessions. The implementation will include project planning, technical configuration, user enablement, compliance alignment, stakeholder engagement, and post-go-live support. The objective is to deliver a production-ready implementation that meets the Reseller's compliance, business, operational, and onboarding requirements.

2. Scope of Services

FrankieOne will deliver the following services to the Reseller:

2.1. Project Planning and Discovery

FrankieOne and the Reseller will conduct an initial project kick-off and discovery workshops with the Reseller's stakeholders. In such workshops, FrankieOne and the Reseller will align on implementation goals, regulatory use cases, and integration points. Additionally, FrankieOne and the Reseller will Identify customer onboarding and risk monitoring objectives. Such workshops will be documented and provide agreement on technical and business requirements.

2.2. Environment and Account Configuration

FrankieOne will provision a test ("UAT") and production environments for the Reseller. During configuration, FrankieOne will set up of one (1) parent account and one (1) child account per environment. FrankieOne will configure the Reseller's accounts to include the required modules and services:

a) Identity Verification (IDV);
b) Branding configurations (white-label UI, logo, colours);
c) KYC data sources and orchestration settings;
d) AML screening rules, including PEP, sanctions, and adverse media lists;
e) Baseline transaction monitoring rules;
f) Customer Risk Rating (Standard CRR model); and
g) Agreed orchestration workflow ("Recipe").

2.3. Technical Integration Support

FrankieOne will provide API documentation and developer guidance throughout integration and support the Reseller's technical teams with integration to FrankieOne APIs. FrankieOne will validate integration logic and conduct walkthroughs as needed.

2.4. Testing and Validation

FrankieOne will assist in System Integration Testing (SIT) by providing test scenarios, data, and validation support. FrankieOne will also provide support during User Acceptance Testing (UAT) and Production Verification Testing (PVT), where FrankieOne will address defects or configuration issues raised during testing phases.

2.5. Training and Enablement

FrankieOne will deliver training sessions to administrators and core users. FrankieOne will provide the Reseller with FrankieOne user guides, compliance enablement materials, and workflow documentation. During Training and Enablement, FrankieOne will clarify usage boundaries, audit access, and escalation paths for various users.

2.6. Go-Live and Hypercare Support

FrankieOne will deploy configured workflows and rules to the production environment and provide support to the Reseller during the first two (2) weeks post go-live ("Hypercare Period") to Resolve early-life issues and ensure transition to operational support.

2.7. Compliance and Governance Alignment

FrankieOne will map FrankieOne's platform capabilities against the Reseller's regulatory use cases (e.g., AML/CTF, eKYC, transaction monitoring). FrankieOne will support the Reseller's internal compliance stakeholder sessions to validate end-to-end audit trails, recordkeeping, and reporting pathways. During Compliance and Governance Alignment, the parties will confirm the Reseller's readiness for onboarding regulated customers.

2.8. Stakeholder Communication and Reporting

Provide weekly status reports and progress updates.
- Facilitate regular steering meetings to track key risks, issues, and decision points.
- Coordinate between Reseller project, compliance, and IT teams as required.

2.9 Disclaimers
The parties acknowledge and agree that:
i) any delays caused by the Reseller (including failure to provide required information, feedback, or approvals within agreed timeframes) may result in adjustments to the project timeline and/or additional Fees; and
ii) FrankieOne's obligations under this SOW are subject to the Reseller's compliance with its obligations under the Agreement and this SOW.

3. Out of Scope
The following services are explicitly excluded from the scope of this SOW unless otherwise agreed in writing:
a) Configuration of custom risk models or workflows beyond the standard CRR model;
b) Development of custom features or services not currently supported by the FrankieOne API;
c) Data migration services from the Reseller's legacy systems;
d) Integration with third-party platforms or services outside of those offered within FrankieOne's platform;
e) Hosting or support of any customer-managed systems; and
f) End-user onboarding and customer success management post go-live.

4. Change Management
4.1. Change Requests
Either party may request changes to the scope, timeline, or deliverables of this SOW by submitting a written Change Request. All Change Requests must include a description of the proposed change, the reason for the change, and any anticipated impact on cost, timeline, or deliverables.
4.2. Impact Assessment
Upon receipt of a Change Request, FrankieOne will conduct an impact assessment and provide the Reseller with an estimate of the effect on project cost, timeline, resources, and deliverables within five (5) business days. Both parties agree to negotiate any changes in good faith and in a timely manner.
4.3. Approval and Implementation
No change shall be implemented until the Change Request has been approved in writing by authorised representatives of both FrankieOne and the Reseller. Approved changes will be documented as an amendment to this SOW.
4.4. Disputed Changes
In the event of disagreement regarding a proposed change, the matter shall be escalated in accordance with the escalation procedures outlined in the Agreement. Pending resolution, both parties will continue to perform their obligations under the existing SOW unless otherwise agreed.
4.5. Minor Adjustments
Minor adjustments that do not materially affect the scope, cost, or timeline of the project may be agreed upon informally between the respective Project Managers, provided such adjustments are documented in the project status report.

5. Reseller Obligations
To enable FrankieOne to perform its obligations under this SOW effectively, the Reseller agrees:
i) to provide timely access to systems, environments, data, and personnel as reasonably required by FrankieOne to perform the services;
ii) that it will be responsible for obtaining any third-party consents, licences, or approvals necessary for FrankieOne to perform the services;
iii) that it will assign a dedicated project manager or coordinator to act as FrankieOne's primary point of contact;

6. Acceptance
Unless otherwise specified, deliverables under this SOW will be deemed accepted if the Reseller does not provide written notice of rejection (with reasonable detail of deficiencies) within ten (10) business days of delivery. If a deliverable is rejected, FrankieOne will use reasonable efforts to address the identified deficiencies and resubmit the deliverable within a mutually agreed timeframe.`

export const getDefaultRaciTemplate = (): { columns: RaciColumn[]; entries: RaciEntry[] } => {
  const now = Date.now()
  const columns: RaciColumn[] = [
    { id: `col_${now}_pm`, name: 'FrankieOne PM' },
    { id: `col_${now}_presales`, name: 'FrankieOne Presales' },
    { id: `col_${now}_impl`, name: 'FrankieOne Impl Lead' },
    { id: `col_${now}_cs`, name: 'FrankieOne CS' },
    { id: `col_${now}_client_pm`, name: 'Client PM' },
    { id: `col_${now}_client_tech`, name: 'Client Tech Lead' },
  ]

  // Order: PM, Presales, Impl Lead, CS, Client PM, Client Tech
  const mk = (...vals: RaciValue[]): Record<string, RaciValue> => {
    const result: Record<string, RaciValue> = {}
    columns.forEach((col, i) => { result[col.id] = vals[i] || '' })
    return result
  }

  const entries: RaciEntry[] = [
    { id: now,     task: 'Project Kick-off',                    assignments: mk('R', 'C', 'A', 'I', 'C', 'I') },
    { id: now + 1, task: 'Discovery Workshops',                 assignments: mk('R', 'R', 'A', 'C', 'C', 'C') },
    { id: now + 2, task: 'Requirements Capture',                assignments: mk('C', 'R', 'A', 'I', 'C', 'C') },
    { id: now + 3, task: 'Environment Setup',                   assignments: mk('I', 'I', 'R', 'I', 'I', 'A') },
    { id: now + 4, task: 'Configuration',                       assignments: mk('I', 'C', 'R', 'I', 'C', 'A') },
    { id: now + 5, task: 'Custom Work / Integration Support',   assignments: mk('I', 'C', 'R', 'I', 'I', 'A') },
    { id: now + 6, task: 'UAT Testing',                         assignments: mk('C', 'I', 'A', 'I', 'R', 'R') },
    { id: now + 7, task: 'Go-Live',                             assignments: mk('R', 'I', 'A', 'C', 'C', 'C') },
    { id: now + 8, task: 'Hypercare Support',                   assignments: mk('I', 'I', 'R', 'A', 'I', 'C') },
    { id: now + 9, task: 'Handover to CS',                      assignments: mk('C', 'I', 'I', 'R', 'I', 'I') },
  ]

  return { columns, entries }
}

export const getDefaultGanttTemplate = (): GanttTask[] => {
  const now = Date.now()
  return [
    { id: now,     phaseId: 0, taskName: 'Project Kick-off & Discovery', startDate: '', endDate: '', dependencies: [], resources: [], responsiblePerson: '', party: 'frankieone' },
    { id: now + 1, phaseId: 0, taskName: 'Environment Provisioning',     startDate: '', endDate: '', dependencies: [], resources: [], responsiblePerson: '', party: 'frankieone' },
    { id: now + 2, phaseId: 0, taskName: 'Platform Configuration',       startDate: '', endDate: '', dependencies: [], resources: [], responsiblePerson: '', party: 'frankieone' },
    { id: now + 3, phaseId: 0, taskName: 'API Integration',              startDate: '', endDate: '', dependencies: [], resources: [], responsiblePerson: '', party: 'client' },
    { id: now + 4, phaseId: 0, taskName: 'SIT Testing',                  startDate: '', endDate: '', dependencies: [], resources: [], responsiblePerson: '', party: 'frankieone' },
    { id: now + 5, phaseId: 0, taskName: 'UAT Testing',                  startDate: '', endDate: '', dependencies: [], resources: [], responsiblePerson: '', party: 'client' },
    { id: now + 6, phaseId: 0, taskName: 'Go-Live Deployment',           startDate: '', endDate: '', dependencies: [], resources: [], responsiblePerson: '', party: 'frankieone' },
    { id: now + 7, phaseId: 0, taskName: 'Hypercare Period',             startDate: '', endDate: '', dependencies: [], resources: [], responsiblePerson: '', party: 'frankieone' },
  ]
}

export const getDefaultSowConfig = (sowType?: string): SowConfig => ({
  legalText: (sowType === 'reseller_parent' || sowType === 'reseller_child')
    ? DEFAULT_RESELLER_SOW_LEGAL_TEXT
    : DEFAULT_SOW_LEGAL_TEXT,
  deliveryNotes: '',
  milestones: [],
  phases: [],
  commercialsPricingModel: '',
  commercialsFixedFee: '',
  commercialsTmRate: '',
  commercialsTmCap: '',
  raciColumns: [],
  raciEntries: [],
  ganttTasks: [],
  activities: [],
  keyAssumptions: '',
  sequenceDiagramDsl: '',
  notes: '',
  implementationsManager: '',
})

export const getDefaultScopeData = (): ScopeData => ({
  customerName: '',
  customerAbn: '',
  hubspotDealId: null,
  hubspotDealName: null,
  generalInfo: getDefaultGeneralInfo(),
  blueprintConfig: getDefaultBlueprintConfig(),
  kycConfig: getDefaultKycConfig(),
  amlConfig: getDefaultAmlConfig(),
  biometricsConfig: getDefaultBiometricsConfig(),
  kybConfig: getDefaultKybConfig(),
  recipesConfig: [],
  workflowsConfig: [],
  rulesConfig: getDefaultRulesConfig(),
  riskConfig: getDefaultRiskConfig(),
  sowConfig: getDefaultSowConfig(),
  sowCustomizedSections: [],
  scopeHours: [],
  featureRequests: [],
  workflowsNotes: '',
  workflowsRequiredCount: null,
  notes: '',
  finalized: false,
  finalizedAt: null,
  finalizedBy: null,
})
